import { useState, useRef, useEffect } from 'react';
import usePageMeta from '@/hooks/usePageMeta';
import { useChatbotOffset } from '@/hooks/useChatbotOffset';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import RecruitHero from './components/RecruitHero';
import RecruitMessage from './components/RecruitMessage';
import RecruitFeatures from './components/RecruitFeatures';
import RecruitData from './components/RecruitData';
import RecruitVoices from './components/RecruitVoices';
import RecruitRequirements from './components/RecruitRequirements';
import RecruitEntry from './components/RecruitEntry';
import { RECRUIT_SECTIONS } from './data/recruitData';

const RecruitPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const chatbotOffset = useChatbotOffset();

  usePageMeta({
    title: '採用情報 | 株式会社MSK',
    ogTitle: '採用情報 | 株式会社MSK',
    description: '理想の未来はここで叶えよう。株式会社MSKの採用情報・募集要項をご覧いただけます。',
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-white font-['Noto_Sans_JP']">
      <Header />
      <main>
        <RecruitHero />
        <RecruitMessage />
        <RecruitFeatures />
        <RecruitData />
        <RecruitVoices />
        <RecruitRequirements />
        <RecruitEntry />
      </main>
      <Footer />

      {/* ページ内ジャンプメニュー */}
      <div
        ref={menuRef}
        style={{ position: 'fixed', bottom: chatbotOffset, right: '20px', zIndex: 9999 }}
        className="flex flex-col items-end gap-2"
      >
        {menuOpen && (
          <div className="flex flex-col gap-1.5 mb-1 items-end">
            <button
              type="button"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setMenuOpen(false);
              }}
              className="flex items-center gap-2.5 bg-white text-[#89B56B] text-xs font-bold px-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.12)] hover:bg-[#89B56B] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap border border-[#89B56B]/20"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-up-line text-sm"></i>
              </span>
              ページトップ
            </button>

            {RECRUIT_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2.5 bg-white text-[#89B56B] text-xs font-bold px-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.12)] hover:bg-[#89B56B] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap border border-[#89B56B]/20"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${section.icon} text-sm`}></i>
                </span>
                {section.label}
              </a>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          style={{ width: 48, height: 48, backgroundColor: menuOpen ? '#5DAA68' : '#89B56B' }}
          className="text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center cursor-pointer"
          aria-label="ページ内メニュー"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
        </button>
      </div>
    </div>
  );
};

export default RecruitPage;
